import React, { FunctionComponent, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Spinner } from '@yandex-360/orb-components/dist/components/Spinner';
import { CameraFacingMode } from '@yandex-360/telemost-sdk/device-manager';
import { LoggableError } from '@yandex-int/messenger.errors';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { checkBlackScreen } from '../../../../../helpers/black-screen-detector';
import { createLogger } from '../../../../../helpers/loggerFacade';
import { useActiveDeviceStream } from '../../../../device-manager/hooks';
import { getVideoInputError } from '../../../../device-manager/selectors';
import { deviceManager } from '../../../../goloom-sdk';
import { selectVirtualBackgroundType } from '../../../../settings';
import css from './styles.css';

const logger = createLogger('video-preview');

export interface VideoPreviewProps {
    deviceIdOrFacingMode?: CameraFacingMode | string;
}

export const VideoPreview: FunctionComponent<VideoPreviewProps> = ({ deviceIdOrFacingMode }) => {
    const videoInputError = useSelector(getVideoInputError);

    const videoRef = useRef<HTMLVideoElement>(null);

    const virtualBackgroundType = useSelector(selectVirtualBackgroundType);

    const [mediaStream, setMediaStream] = useState<Maybe<MediaStream>>();
    const [isVideoReady, setIsVideoReady] = useState<boolean>(false);
    const activeDeviceMediaStream = useActiveDeviceStream('videoinput', true);

    useEffect(() => setIsVideoReady(false), [deviceIdOrFacingMode]);

    useLayoutEffect(() => {
        if (videoRef.current) {
            videoRef.current.srcObject = activeDeviceMediaStream ?? mediaStream ?? null;
        }
    }, [mediaStream, activeDeviceMediaStream]);

    useLayoutEffect(() => {
        if (deviceManager.videoStatus) {
            return;
        }
        const cameraStreamPromise = deviceManager.getLocalPreviewCameraStream(deviceIdOrFacingMode);

        cameraStreamPromise.then((cameraStream) => {
            setMediaStream(cameraStream ?? undefined);
        });
        return () => {
            deviceManager.stopPreviewVideoStream();
        };
    }, [deviceIdOrFacingMode, virtualBackgroundType]);

    useLayoutEffect(() => {
        const currentVideoRef = videoRef.current;
        if (currentVideoRef) {
            let timer: Maybe<ReturnType<typeof setTimeout>>;
            let blackScreenCheck: ReturnType<typeof checkBlackScreen> | null = null;

            const onCanPlayThrough = () => {
                timer = setTimeout(() => {
                    setIsVideoReady(true);

                    const currentStream = currentVideoRef.srcObject as MediaStream | null;
                    if (!currentStream) {
                        return;
                    }

                    if (blackScreenCheck) {
                        blackScreenCheck.cancel();
                    }

                    const streamId = currentStream.id;
                    const videoTracks = currentStream.getVideoTracks();
                    const firstTrack = videoTracks[0];
                    const trackId = firstTrack?.id;

                    blackScreenCheck = checkBlackScreen(currentVideoRef, 10);

                    blackScreenCheck.promise
                        .then((result) => {
                            if (result.cancelled) {
                                return;
                            }

                            const currentStreamAfterCheck = videoRef.current?.srcObject as MediaStream | null;
                            const currentStreamIdAfterCheck = currentStreamAfterCheck?.id;
                            const currentTrackIdAfterCheck = currentStreamAfterCheck?.getVideoTracks()[0]?.id;

                            if (streamId !== currentStreamIdAfterCheck || trackId !== currentTrackIdAfterCheck) {
                                return;
                            }

                            if (result.isBlack) {
                                const isTrackEnabled: boolean = firstTrack?.enabled ?? false;
                                const trackReadyState: MediaStreamTrackState | undefined = firstTrack?.readyState;
                                const isStreamActive: boolean = currentStream?.active ?? false;
                                const isTrackMuted: boolean = firstTrack?.muted ?? false;
                                const isTrackLive: boolean = trackReadyState === 'live';

                                logger.warn(new Error('Black screen detected') as LoggableError, {
                                    method: 'checkBlackScreen',
                                    additional: {
                                        isStreamActive,
                                        isTrackEnabled,
                                        isTrackMuted,
                                        isTrackLive,
                                        checkResult: {
                                            blackCount: result.blackCount,
                                            totalChecks: result.totalChecks,
                                        },
                                    },
                                });
                            }
                        })
                        .catch((error) => {
                            logger.error(error, {
                                method: 'checkBlackScreen',
                            });
                        });
                }, 100);
            };

            currentVideoRef.addEventListener('canplaythrough', onCanPlayThrough);

            return () => {
                currentVideoRef.removeEventListener('canplaythrough', onCanPlayThrough);

                if (timer) {
                    clearTimeout(timer);
                }
                if (blackScreenCheck) {
                    blackScreenCheck.cancel();
                }
            };
        }
    }, []);

    const withMirror = deviceIdOrFacingMode !== CameraFacingMode.ENVIRONMENT;
    const isSpinVisible = !isVideoReady && !videoInputError;

    return (
        <div className={css.background}>
            <video
                className={cn(css.video, {
                    [css.mirror]: withMirror,
                    [css.hidden]: isSpinVisible,
                })}
                ref={videoRef}
                autoPlay
                playsInline
                // remove YaBro custom buttons
                // eslint-disable-next-line react/no-unknown-property
                x-yandex-pip="false"
            />
            {isSpinVisible && <Spinner size="lg" view="orb" progress />}
        </div>
    );
};
